import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { IonicModule } from '@ionic/angular';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { ChartOptions } from 'chart.js';
import moment from 'moment';
import { BaseChartDirective } from 'ng2-charts';

import {
  CHART_DATE_DIMENSIONS,
  CHART_TYPE_VALUES,
  CONVERSATION_PRIORITY_VALUES,
  CONVERSATION_STATUS,
  UNIT,
} from 'src/app/constants';
import { getRandomColor } from 'src/app/utils/utils';
import { SavedReport } from 'src/models';
import { ApiService } from 'src/services/api.service';

export interface ChartConfig {
  chartType: string;
  chartData: any;
  chartOptions: ChartOptions;
  error?: string;
}

@Component({
  selector: 'app-chart',
  templateUrl: './chart.component.html',
  styleUrls: ['./chart.component.scss'],
  standalone: true,
  imports: [CommonModule, IonicModule, BaseChartDirective, NgxDatatableModule],
  styles: [
    `
      .table-view {
        height: 100%;
        overflow: auto;
      }

      .data-table {
        width: 100%;
        border-collapse: collapse;
      }

      .data-table th,
      .data-table td {
        padding: 8px;
        text-align: left;
        border-bottom: 1px solid #ddd;
      }

      .data-table th {
        background-color: #f2f2f2;
        position: sticky;
        top: 0;
      }
    `,
  ],
})
export class ChartComponent implements OnInit, OnChanges {
  @Input() savedReportId: string;
  @Input() savedReport: SavedReport;
  @Input() showLegend: boolean = true;
  @Input() showTable: boolean = false;

  chartConfig: ChartConfig = {
    chartType: 'line',
    chartData: {
      labels: [],
      datasets: [],
    },
    chartOptions: {
      responsive: true,
      animation: { duration: 600 },
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
        },
        y: {
          ticks: {
            stepSize: 1,
          },
        },
      },
    },
  };
  chartType: string;
  unit = UNIT.MINUTES;
  dimensionKey: string;
  lookups: Record<string, Record<string, string>>;

  // Add tabular data for the table view
  tabularData: any[] = [];
  showTotalColumn = false;
  totalValues: any[] = [];

  isLoading = false;

  constructor(private apiService: ApiService) {}

  ngOnInit() {
    if (this.savedReportId) {
      this.loadSavedReport();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.savedReportId && !changes.savedReportId.firstChange) {
      this.loadReportData();
    }

    // Update chart options when display settings change
    if (
      (changes.showLegend && !changes.showLegend.firstChange) ||
      (changes.showTable && !changes.showTable.firstChange)
    ) {
      this.updateChartOptions();
    }
  }

  // Add method to update chart options based on display settings
  updateChartOptions() {
    if (this.chartConfig && this.chartConfig.chartOptions && this.chartConfig.chartOptions.plugins) {
      this.chartConfig.chartOptions.plugins.legend = {
        display: this.showLegend,
      };
    }
  }

  async loadSavedReport() {
    // this.savedReport = await this.apiService.getPromise(`/saved-reports/${this.savedReportId}`);
    this.chartType = this.savedReport.metric;
    this.unit = Array.from(Object.values(UNIT)).find(
      (u) => u.value === this.savedReport.displaySettings.unitConversion,
    );
    this.dimensionKey = this.savedReport.dimensions?.length ? this.savedReport.dimensions[0] : undefined;
    this.chartConfig.chartType = this.savedReport.displaySettings?.chartStyle || 'line';

    // Update chart options based on display settings
    this.updateChartOptions();

    this.loadReportData();
  }

  async loadReportData() {
    if (!this.savedReportId) return;

    this.isLoading = true;
    this.chartConfig.error = undefined;

    try {
      // Get the saved report details if not provided
      this.isLoading = true;

      const params = {
        dateDimension: this.savedReport.dateDimension,
        dateRange: this.savedReport.dateRange,
        metric: this.savedReport.metric,
        timezone: moment.tz.guess(),
        dimensions: this.savedReport.dimensions,
        filters: this.savedReport.filters,
      };

      this.showTotalColumn =
        this.savedReport.metric === CHART_TYPE_VALUES.TOTAL_COUNT &&
        this.savedReport.dateDimension !== CHART_DATE_DIMENSIONS.TOTAL;

      const rawData: any = await this.apiService.postPromise(`/reporting/time-series`, params);
      this.lookups = rawData.lookups;
      const columns = rawData.dates;
      const timeSeriesData = rawData.data;
      const tabularData = (this.dimensionKey ? Object.keys(timeSeriesData) : [undefined])
        .map((value) => {
          const row = {
            dimensionLabel: this.getValue(value, this.dimensionKey),
            dimension: value,
          };

          for (const date of columns) {
            row[date] = this.getCellValue(value, date, timeSeriesData);
          }

          return row;
        })
        .filter((row) => {
          if (this.dimensionKey === 'tagId') {
            return row.dimension !== 'null';
          }

          return true;
        });

      const labels: Set<string> = new Set();
      const values = new Map();
      const dateFormat = this.getDateFormat();

      // Prepare tabular data for the table view and cards
      this.tabularData = [];

      for (const row of tabularData) {
        const dimension = row.dimension;
        const dimensionLabel = row.dimensionLabel;
        let rowTotal = 0;

        // Create a new row for the table
        const tableRow = {
          dimension: dimension,
          dimensionLabel: dimensionLabel,
          dimensionData: {
            value: dimensionLabel,
            icon: this.getDimensionIcon(dimension),
          },
          total: rowTotal,
        };

        for (const dateString of columns) {
          const value = row[dateString] ?? 0;
          const formattedDate = moment(dateString).format(dateFormat);

          // Add the value to the table row
          tableRow[formattedDate] = value;

          labels.add(formattedDate);

          if (!values.get(dimension)) {
            values.set(dimension, []);
          }

          values.get(dimension).push(value);

          rowTotal += value;
        }

        tableRow.total = rowTotal;

        this.tabularData.push(tableRow);
      }

      this.setTotalValuesLocally();

      const chartData = [];

      for (const [dimension, v] of values.entries()) {
        const color = this.getColor(dimension);
        chartData.push({
          type: this.chartConfig.chartType,
          fill: true,
          data: v,
          backgroundColor: (ctx, options) => {
            if (this.chartConfig.chartType === 'bar') {
              return color;
            }
            const grad = ctx.chart.ctx.createLinearGradient(0, 0, 0, 300);
            grad.addColorStop(0, color);
            grad.addColorStop(1, '#ffffff00');
            ctx.chart.ctx.fillStyle = grad;
            return grad;
          },
          borderColor: color,
          tension: this.savedReport.displaySettings.lineSmoothing ? 0.3 : 0,
          label: this.getValue(dimension, this.dimensionKey),
          offset: this.chartConfig.chartType === 'bar' ? true : false,
        });
      }

      this.chartConfig.chartData.datasets = chartData;
      this.chartConfig.chartData.labels = Array.from(labels);
      this.isLoading = false;
    } catch (error) {
      console.error('Error loading report data:', error);
      this.isLoading = false;
      this.chartConfig.error = 'Failed to load report data';
    }
  }

  setTotalValuesLocally() {
    if (!this.tabularData.length) {
      return [];
    }

    this.totalValues = this.tabularData.map((row) => ({
      label: row.dimensionLabel,
      value: row.total,
      dimensionData: row.dimensionData,
      isTotalsRow: row.isTotalsRow,
    }));
  }

  getYAxisLabel() {
    if (this.chartType === CHART_TYPE_VALUES.TOTAL_COUNT) {
      return 'Conversation count';
    } else if (
      this.chartType === CHART_TYPE_VALUES.RESPONSE_TIME_MEAN ||
      this.chartType === CHART_TYPE_VALUES.RESPONSE_TIME_P95 ||
      this.chartType === CHART_TYPE_VALUES.RESPONSE_TIME_P99 ||
      this.chartType === CHART_TYPE_VALUES.RESPONSE_TIME_MEDIAN ||
      this.chartType === CHART_TYPE_VALUES.RESPONSE_TIME_WORKING_MEAN ||
      this.chartType === CHART_TYPE_VALUES.RESPONSE_TIME_WORKING_MEDIAN ||
      this.chartType === CHART_TYPE_VALUES.RESPONSE_TIME_WORKING_P95 ||
      this.chartType === CHART_TYPE_VALUES.RESPONSE_TIME_WORKING_P99
    ) {
      return `First response time (${this.unit.label.toLowerCase()})`;
    } else if (
      this.chartType === CHART_TYPE_VALUES.RESOLUTION_TIME_MEAN ||
      this.chartType === CHART_TYPE_VALUES.RESOLUTION_TIME_P95 ||
      this.chartType === CHART_TYPE_VALUES.RESOLUTION_TIME_P99 ||
      this.chartType === CHART_TYPE_VALUES.RESOLUTION_TIME_MEDIAN ||
      this.chartType === CHART_TYPE_VALUES.RESOLUTION_TIME_WORKING_MEAN ||
      this.chartType === CHART_TYPE_VALUES.RESOLUTION_TIME_WORKING_MEDIAN ||
      this.chartType === CHART_TYPE_VALUES.RESOLUTION_TIME_WORKING_P95 ||
      this.chartType === CHART_TYPE_VALUES.RESOLUTION_TIME_WORKING_P99
    ) {
      return `Resolution time (${this.unit.label.toLowerCase()})`;
    } else if (this.chartType === CHART_TYPE_VALUES.CLOSED_COUNT) {
      return 'Resolution count';
    } else if (this.chartType === CHART_TYPE_VALUES.SENTIMENT_SCORE_MEAN) {
      return 'Sentiment score';
    } else if (this.chartType === CHART_TYPE_VALUES.ESCALATION_COUNT) {
      return 'Escalation count';
    } else if (
      this.chartType === CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_MEAN ||
      this.chartType === CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_MEDIAN ||
      this.chartType === CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_P95 ||
      this.chartType === CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_P99 ||
      this.chartType === CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_WORKING_MEAN ||
      this.chartType === CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_WORKING_MEDIAN ||
      this.chartType === CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_WORKING_P95 ||
      this.chartType === CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_WORKING_P99
    ) {
      return `Waiting on agent time (${this.unit.label.toLowerCase()})`;
    } else if (
      this.chartType === CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_MEAN ||
      this.chartType === CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_MEDIAN ||
      this.chartType === CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_P95 ||
      this.chartType === CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_P99 ||
      this.chartType === CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_WORKING_MEAN ||
      this.chartType === CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_WORKING_MEDIAN ||
      this.chartType === CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_WORKING_P95 ||
      this.chartType === CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_WORKING_P99
    ) {
      return `Waiting on customer time (${this.unit.label.toLowerCase()})`;
    } else if (
      this.chartType === CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_MEAN ||
      this.chartType === CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_WORKING_MEAN ||
      this.chartType === CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_MEDIAN ||
      this.chartType === CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_WORKING_MEDIAN ||
      this.chartType === CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_P95 ||
      this.chartType === CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_WORKING_P95 ||
      this.chartType === CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_P99 ||
      this.chartType === CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_WORKING_P99
    ) {
      return `Ticket to escalation time (${this.unit.label.toLowerCase()})`;
    } else if (
      this.chartType === CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_MEAN ||
      this.chartType === CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_WORKING_MEAN ||
      this.chartType === CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_MEDIAN ||
      this.chartType === CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_WORKING_MEDIAN ||
      this.chartType === CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_P95 ||
      this.chartType === CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_WORKING_P95 ||
      this.chartType === CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_P99 ||
      this.chartType === CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_WORKING_P99
    ) {
      return `Escalation to acknowledgement time (${this.unit.label.toLowerCase()})`;
    } else if (
      this.chartType === CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_MEAN ||
      this.chartType === CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_WORKING_MEAN ||
      this.chartType === CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_MEDIAN ||
      this.chartType === CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_WORKING_MEDIAN ||
      this.chartType === CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_P95 ||
      this.chartType === CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_WORKING_P95 ||
      this.chartType === CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_P99 ||
      this.chartType === CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_WORKING_P99
    ) {
      return `Acknowledgement to resolution time (${this.unit.label.toLowerCase()})`;
    }

    return 'No data';
  }

  getValue(value, dimensionKey) {
    if (!dimensionKey) {
      return this.getYAxisLabel();
    }

    const dimensionLookups = this.lookups[dimensionKey];

    if (dimensionLookups) {
      return dimensionLookups[value] ?? value;
    }

    return value;
  }

  getCellValue(value, column, data) {
    const rawMetric = (value != null ? data[value][column] : data[column]) ?? 0;

    return [
      CHART_TYPE_VALUES.TOTAL_COUNT,
      CHART_TYPE_VALUES.CLOSED_COUNT,
      CHART_TYPE_VALUES.SENTIMENT_SCORE_MEAN,
      CHART_TYPE_VALUES.ESCALATION_COUNT,
    ].includes(this.chartType)
      ? rawMetric
      : Math.ceil((rawMetric as number) / this.unit.value);
  }

  getColumnName(column) {
    if (this.savedReport.dateDimension === CHART_DATE_DIMENSIONS.TOTAL) {
      return 'Total';
    }

    return moment(column).format(this.getDateFormat());
  }

  getDateFormat() {
    return 'MMM D YYYY';
  }

  getColor(name: string) {
    const map = {
      [CONVERSATION_STATUS.OPEN]: '#cc0022aa',
      [CONVERSATION_STATUS.IN_PROGRESS]: '#cc9922aa',
      [CONVERSATION_STATUS.ON_HOLD]: '#0000ffaa',
      [CONVERSATION_STATUS.CLOSED]: '#00cc22aa',
      [CONVERSATION_PRIORITY_VALUES.LOW]: '#00cc22aa',
      [CONVERSATION_PRIORITY_VALUES.MEDIUM]: '#0000ccaa',
      [CONVERSATION_PRIORITY_VALUES.HIGH]: '#cc9922aa',
      [CONVERSATION_PRIORITY_VALUES.CRITICAL]: '#cc0022aa',
      null: '#88888855',
    };

    return map[name] ?? getRandomColor();
  }

  // Helper method to get an icon for a dimension
  getDimensionIcon(dimension: string): string {
    // Map dimensions to icons - customize based on your needs
    const iconMap = {
      status: 'pulse',
      assigneeId: 'person',
      customerId: 'business',
      tagId: 'pricetag',
      priority: 'alert-circle',
      sourceType: 'chatbubbles',
      default: 'analytics',
    };

    return iconMap[this.dimensionKey] || iconMap.default;
  }
}
